(() => {
  const GLOBAL_KEY = '__rakovalisMetaConsentRevocationV1';
  if (window[GLOBAL_KEY]?.register) return;

  const script = document.currentScript;
  const revokeUrl = script?.dataset.revokeUrl;
  const storageKey = 'rakovalis-meta-consent-revocations-v1';
  const REVOCATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const entryPattern = /^entry_[a-f0-9]{40}$/;
  const tokenPattern = /^mrv1_[a-f0-9]{64}$/;
  let memoryEntries = [];
  let flushing = false;

  const validCredential = (value) =>
    value && entryPattern.test(value.entryId) && tokenPattern.test(value.token);

  const validStoredEntry = (value) =>
    validCredential(value) &&
    Number.isFinite(value.expiresAt) &&
    value.expiresAt > Date.now();

  const readEntries = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(parsed)) return [];
      const entries = parsed.filter(validStoredEntry).slice(-32);
      if (entries.length !== parsed.length) {
        window.localStorage.setItem(storageKey, JSON.stringify(entries));
      }
      return entries;
    } catch (_error) {
      return memoryEntries;
    }
  };

  const writeEntries = (entries) => {
    const sanitized = entries.filter(validStoredEntry).slice(-32);
    memoryEntries = sanitized;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(sanitized));
    } catch (_error) {
      // Keep a same-page fallback when persistent storage is unavailable.
    }
  };

  const loadCustomerPrivacy = () =>
    new Promise((resolve) => {
      if (window.Shopify?.customerPrivacy) {
        resolve(window.Shopify.customerPrivacy);
        return;
      }
      if (typeof window.Shopify?.loadFeatures !== 'function') {
        resolve(null);
        return;
      }
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => resolve(error ? null : window.Shopify?.customerPrivacy || null),
        );
      } catch (_error) {
        resolve(null);
      }
    });

  const revokedPrivacy = (privacyApi) => {
    try {
      const consent = privacyApi?.currentVisitorConsent?.();
      if (!consent || (consent.marketing !== 'no' && consent.analytics !== 'no')) {
        return null;
      }
      return {
        marketing_allowed:
          consent.marketing === 'yes' && privacyApi?.marketingAllowed?.() === true,
        analytics_allowed:
          consent.analytics === 'yes' &&
          privacyApi?.analyticsProcessingAllowed?.() === true,
      };
    } catch (_error) {
      return null;
    }
  };

  const flushRevocations = async () => {
    if (flushing || !revokeUrl) return;
    const entries = readEntries();
    if (!entries.length) return;

    const privacy = revokedPrivacy(await loadCustomerPrivacy());
    if (!privacy) return;

    flushing = true;
    const completed = new Set();
    try {
      for (const entry of entries) {
        try {
          const response = await fetch(revokeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            keepalive: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Rakovalis-Consent-Revocation': '1',
            },
            body: JSON.stringify({
              schema_version: 1,
              entry_id: entry.entryId,
              revocation_token: entry.token,
              privacy: { signal_version: 2, ...privacy },
            }),
          });
          if (response.ok) completed.add(`${entry.entryId}:${entry.token}`);
        } catch (_error) {
          // Keep the token so a later page view can retry safely.
        }
      }
    } finally {
      if (completed.size) {
        writeEntries(
          readEntries().filter(
            (entry) => !completed.has(`${entry.entryId}:${entry.token}`),
          ),
        );
      }
      flushing = false;
    }
  };

  const register = (entryId, token) => {
    const entry = { entryId, token, expiresAt: Date.now() + REVOCATION_TTL_MS };
    if (!validCredential(entry)) return;
    const entries = readEntries().filter((item) => item.entryId !== entryId);
    entries.push(entry);
    writeEntries(entries);
  };

  window[GLOBAL_KEY] = { register };
  document.addEventListener('visitorConsentCollected', () => {
    void flushRevocations();
  });
  void flushRevocations();
})();
